import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import swal from 'sweetalert2';
import { ConsultaList } from 'src/app/shared/model/core/ConsultaList.model';
import { Servidor } from 'src/app/shared/model/fise/servidor.model';
import { FiseService } from '../fise.service';
import { ModalServidorComponent } from './modal-servidor/modal-servidor.component';
import { SoloDireccionesIpDirective } from '../../utilerias/directives/solo-direcciones-ip.directive';

@Component({
  selector: 'app-gestion-servidores',
  standalone: true,
  templateUrl: './gestion-servidores.component.html',
  imports: [
    NgFor,
    NgIf,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    SoloDireccionesIpDirective,
  ],
})
export class GestionServidoresComponent implements OnInit {
  listServidor: Array<Servidor> = [];
  consultaObject = new ConsultaList<Servidor>();
  servidorReq = new Servidor();
  totalRegistros = 0;
  currentPage = 1;
  pageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];
  loading = false;
  filterIp = '';
  modalRefServidor: MatDialogRef<any>;

  constructor(
    private modalDialog: MatDialog,
    private servidorService: FiseService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.consultaObject.bean = this.servidorReq;
    this.consultaPagina(1);
  }

  consultaPagina(page: number): void {
    this.currentPage = Math.max(1, page);
    this.loading = this.listServidor.length === 0;
    this.cdr.detectChanges();

    this.servidorReq.ip = (this.filterIp ?? '').trim();
    this.consultaObject.bean = this.servidorReq;
    this.consultaObject.regInicial = (this.currentPage - 1) * this.pageSize;
    this.consultaObject.regXPagina = this.pageSize;

    forkJoin({
      total: this.servidorService.ConsultaNumServidores(this.servidorReq),
      servidores: this.servidorService.consultarServidorPorIPPag(this.consultaObject),
    }).subscribe({
      next: ({ total, servidores }) => {
        this.totalRegistros = this.resolveTotalRegistros(total, servidores);
        this.listServidor = Array.isArray(servidores?.list) ? [...servidores.list] : [];

        const maxPages = this.totalPages;
        if (maxPages > 0 && this.currentPage > maxPages) {
          this.consultaPagina(maxPages);
          return;
        }

        if (maxPages === 0) {
          this.currentPage = 1;
        }

        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.listServidor = [];
        this.totalRegistros = 0;
        this.currentPage = 1;
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  onFiltroIpChange(): void {
    this.consultaPagina(1);
  }

  onPageSizeChange(value: number): void {
    this.pageSize = Number(value);
    this.consultaPagina(1);
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.totalPages) {
      return;
    }
    this.consultaPagina(page);
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  onClicMostrarModalServidor(servidorEdit: Servidor | null): void {
    const servidor = servidorEdit ? servidorEdit : new Servidor();
    if (this.modalDialog.openDialogs.length > 0) {
      return;
    }

    this.modalRefServidor = this.modalDialog.open(ModalServidorComponent, {
      disableClose: true,
      autoFocus: true,
      width: '860px',
      maxWidth: 'calc(100vw - 32px)',
      panelClass: ['custom-modalbox', 'fise-form-dialog'],
      data: servidor,
    });

    this.modalRefServidor.afterClosed().subscribe((value) => {
      if (value !== null && value !== undefined) {
        this.consultaPagina(this.currentPage);
      } else {
        this.cdr.detectChanges();
      }
    });
  }

  onClicBorrarServidor(servidor: Servidor): void {
    swal
      .fire({
        title: 'Alerta',
        text: '¿Desea borrar este servidor? Se eliminarán los registros asociados (usuarios y aplicaciones).',
        buttonsStyling: false,
        icon: 'warning',
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-info',
        },
        confirmButtonText: 'Aceptar',
        cancelButtonText: 'Cancelar',
        showConfirmButton: true,
        showCancelButton: true,
      })
      .then((value) => {
        if (!value.value) {
          return;
        }

        this.servidorService.BorrarServidor(servidor).subscribe((response) => {
          if (!response?.pCodRetorno) {
            return;
          }

          swal.fire({
            title: response.pCodRetorno === '00000' ? 'ÉXITO' : 'ERROR',
            text: response.pMensaje,
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-success',
              cancelButton: 'btn btn-info',
            },
            confirmButtonText: 'Aceptar',
            icon: response.pCodRetorno === '00000' ? 'success' : 'warning',
          });

          if (response.pCodRetorno === '00000') {
            this.consultaPagina(this.currentPage);
          }
        });
      });
  }

  onClicLimpiar(): void {
    this.filterIp = '';
    this.consultaPagina(1);
  }

  get totalPages(): number {
    return this.totalRegistros > 0 ? Math.ceil(this.totalRegistros / this.pageSize) : 0;
  }

  get showPagination(): boolean {
    return this.totalRegistros > 0;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([
      1,
      this.totalPages,
      this.currentPage - 1,
      this.currentPage,
      this.currentPage + 1,
    ]);

    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === 'number';
  }

  private resolveTotalRegistros(total: unknown, servidores: ConsultaList<Servidor>): number {
    const totalResponse = total as Record<string, unknown> | null;
    const servidoresResponse = servidores as ConsultaList<Servidor> & Record<string, unknown>;
    const candidates = [
      totalResponse?.pTotalItems,
      totalResponse?.totalItems,
      totalResponse?.totalRegistros,
      servidoresResponse?.pTotalItems,
      servidoresResponse?.totalItems,
      servidoresResponse?.totalRegistros,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    const currentItems = Array.isArray(servidores?.list) ? servidores.list.length : 0;
    if (currentItems === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + currentItems;
  }
}
